import { cloneDeep, isNumber } from 'lodash'

const getBaseSeries = (series, index, summaryData) => {
    const metric = summaryData.metrics?.find((m) => m.name === series.metric)
    const categoryIndex = summaryData.categories?.length === 1 ? index : series.categoryIndex
    const category =
        summaryData.categories?.length === 1 && summaryData.categories[0] === 'All'
            ? null
            : summaryData.categories[series.categoryIndex]
    const baseSeries = {
        name: series.name,
        metric: series.metric,
        categoryIndex,
        category,
        format: metric?.format || null,
    }
    if (summaryData.categoryAxisValues) {
        baseSeries.data = [...(series.data || [])]
    } else {
        baseSeries.data =
            summaryData.dates?.map((date, dateIndex) => [date, series.data?.[dateIndex]]) || []
    }
    return baseSeries
}

const parseColumnSeries = (series, index, summaryConfig, summaryData, themedOverrides) => {
    const columnSeries = getBaseSeries(series, index, summaryData)
    columnSeries.type = 'bar'
    columnSeries.yAxisIndex = series.axis
    if (summaryConfig.stacked) {
        columnSeries.stack = summaryData.categories?.length > 1 ? series.metric : 'All'
    }
    return { ...columnSeries, ...themedOverrides.bar }
}

const parseBarSeries = (series, index, summaryConfig, summaryData, themedOverrides) => {
    const barSeries = getBaseSeries(series, index, summaryData)
    barSeries.type = 'bar'
    barSeries.xAxisIndex = series.axis
    if (summaryConfig.stacked) {
        barSeries.stack = summaryData.categories?.length > 1 ? series.metric : 'All'
    }
    return { ...barSeries, ...themedOverrides.bar }
}

const parseLineSeries = (series, index, summaryData, themedOverrides) => {
    const lineSeries = getBaseSeries(series, index, summaryData)
    lineSeries.type = 'line'
    lineSeries.yAxisIndex = series.axis
    return { ...lineSeries, ...themedOverrides.line }
}

const parseAreaSeries = (series, index, summaryData, themedOverrides) => {
    const areaSeries = getBaseSeries(series, index, summaryData)
    areaSeries.type = 'line'
    areaSeries.areaStyle = {}
    areaSeries.yAxisIndex = series.axis
    return { ...areaSeries, ...themedOverrides?.area }
}

const parsePieSeries = (series, index, summaryData, themedOverrides) => {
    const pieSeries = getBaseSeries(series, index, summaryData)
    pieSeries.type = 'pie'
    pieSeries.data = pieSeries.data.map((value, i) => ({
        value,
        name: summaryData.categoryAxisValues?.[i] || '',
    }))
    return { ...pieSeries, ...themedOverrides.donut }
}

const parseDonutSeries = (series, index, summaryData, themedOverrides) => {
    const donutSeries = getBaseSeries(series, index, summaryData)
    donutSeries.type = 'pie'
    donutSeries.data = donutSeries.data.map((value, i) => ({
        value,
        name: summaryData.categoryAxisValues?.[i] || '',
    }))
    return { ...donutSeries, ...themedOverrides.donut }
}

const parseHeatmapSeries = (series, index, summaryData) => {
    const heatmapSeries = getBaseSeries(series, index, summaryData)
    delete heatmapSeries.categoryIndex
    delete heatmapSeries.category
    heatmapSeries.type = 'heatmap'
    heatmapSeries.data = summaryData.categories.map((catValue, i) => {
        return summaryData.categoryAxisValues.map((cat, j) => {
            return [cat, catValue, summaryData.series[i]?.data[j]]
        })
    })
    return heatmapSeries
}

const parsePictorialSeries = (series, index, summaryData, themedOverrides) => {
    const pictorialSeries = getBaseSeries(series, index, summaryData)
    pictorialSeries.type = 'pictorialBar'
    pictorialSeries.yAxisIndex = series.axis
    if (!themedOverrides.pictorialBar.data[index]) themedOverrides.pictorialBar.data[index] = []
    pictorialSeries.data.forEach((d, i) => {
        if (typeof d === 'number') {
            pictorialSeries.data[i] = Object.assign(
                { value: d },
                themedOverrides.pictorialBar.data[index][i]
            )
        } else {
            pictorialSeries.data[i] = Object.assign(
                {},
                d,
                themedOverrides.pictorialBar.data[index][i]
            )
        }
    })
    if (themedOverrides.pictorialBar.showAsBar) {
        if (pictorialSeries.yAxisIndex !== undefined) {
            pictorialSeries.xAxisIndex = pictorialSeries.yAxisIndex
            delete pictorialSeries.yAxisIndex
        }
    } else {
        if (pictorialSeries.xAxisIndex !== undefined) {
            pictorialSeries.yAxisIndex = pictorialSeries.xAxisIndex
            delete pictorialSeries.xAxisIndex
        }
    }
    pictorialSeries.barGap = themedOverrides.pictorialBar.barGap
    pictorialSeries.barCategoryGap = themedOverrides.pictorialBar.barCategoryGap
    return pictorialSeries
}

const parseGaugeSeries = (series, index, summaryData, themeOverrides) => {
    const gaugeSeries = getBaseSeries(series, index, summaryData)
    gaugeSeries.type = 'gauge'
    gaugeSeries.data = [
        {
            name: summaryData.categoryAxisValues?.[0] || '',
            value:
                (gaugeSeries.data[0] * 100) /
                gaugeSeries.data.reduce((a, b) => a + (b?.value || b), 0),
        },
    ]
    if (themeOverrides.gauge?.data?.[0]?.value && isNumber(themeOverrides.gauge.data[0].value)) {
        gaugeSeries.data = themeOverrides.gauge.data
    } else {
        const name = summaryData.categoryAxisValues?.[0] || ''
        // TODO how to do this?? why is it evaluating in 0??
        const value = (
            ((summaryData?.series[0]?.data[0]?.value || summaryData?.series[0]?.data[0]) * 100) /
            summaryData?.series[0]?.data.reduce((a, b) => a + (b?.value || b), 0)
        ).toFixed(3)
        gaugeSeries.data = [{ value, name }]
    }
    return gaugeSeries
}

const parseRadialBarSeries = (series, index, summaryData, themedOverrides) => {
    const radialBarSeries = getBaseSeries(series, index, summaryData)
    radialBarSeries.type = 'bar'
    radialBarSeries.data = series.data.map((value, i) => ({
        value,
        name: summaryData.categoryAxisValues?.[i] || '',
    }))
    const showAsStacked = themedOverrides.radialBar.stack
    if (showAsStacked) radialBarSeries.stack = 'total'
    radialBarSeries.coordinateSystem = 'polar'
    radialBarSeries.type = 'bar'
    radialBarSeries.label = {
        position: 'middle',
        formatter: '{b}: {c}',
        ...themedOverrides.radialBar.label,
    }
    radialBarSeries.emphasis = themedOverrides.radialBar.emphasis
    radialBarSeries.itemStyle = themedOverrides.radialBar.itemStyle
    radialBarSeries.barWidth = themedOverrides.radialBar.barWidth
    return radialBarSeries
}

const parseScatterSeries = (summaryData) => {
    if (summaryData.metrics?.length < 2) return []
    return (
        summaryData.categories?.map((category, categoryIndex) => {
            const categoryName =
                summaryData.categories.length === 1 && summaryData.categories[0] === 'All'
                    ? null
                    : summaryData.categories[summaryData.series[categoryIndex].categoryIndex]
            const scatterSeries = {
                type: 'scatter',
                categoryIndex:
                    summaryData.categories.length === 1
                        ? 0
                        : summaryData.series[categoryIndex].categoryIndex,
                name: categoryName,
                data: [],
            }
            const firstTwoMetrics = summaryData.metrics.slice(0, 2)
            const matchingDataSeries = firstTwoMetrics.map((metric) =>
                summaryData.series?.find(
                    (series) =>
                        series.metric === metric.name && series.categoryIndex === categoryIndex
                )
            )
            const foundSeriesCount = matchingDataSeries.filter(Boolean).length
            if (foundSeriesCount === 2) {
                const [xSeries, ySeries] = matchingDataSeries
                scatterSeries.data =
                    xSeries.data?.map((xValue, index) => [xValue, ySeries.data?.[index] || 0]) || []
                const xMetric = this.getMetric(xSeries.metric)
                const yMetric = this.getMetric(ySeries.metric)
                scatterSeries.formats = [xMetric?.format || '', yMetric?.format || '']
            } else {
                const availableSeries = matchingDataSeries.find(Boolean)
                const isFirstMetric = matchingDataSeries[0] === availableSeries
                scatterSeries.data =
                    availableSeries?.data?.map((value) =>
                        isFirstMetric ? [value, 0] : [0, value]
                    ) || []
            }
            return scatterSeries
        }) || []
    )
}

const parseWaterfallSeries = (
    series,
    index,
    parsedSeries,
    summaryData,
    themedOverrides,
    defaultTheme
) => {
    const upColor = themedOverrides.waterfall?.upColor || defaultTheme.waterfall.upColor
    const downColor = themedOverrides.waterfall?.downColor || defaultTheme.waterfall.downColor
    const startColor = themedOverrides.waterfall?.startColor || defaultTheme.waterfall.startColor
    const endColor = themedOverrides.waterfall?.endColor || defaultTheme.waterfall.endColor
    const waterfallSeries = getBaseSeries(series, index, summaryData)
    waterfallSeries.type = 'bar'
    waterfallSeries.itemStyle = { color: 'transparent' }
    const positive = {
        name: 'positive',
        type: 'bar',
        stack: 'all',
        data: [],
        itemStyle: { color: upColor },
        waterfallPlaceholder: true,
    }
    const negative = {
        name: 'negative',
        type: 'bar',
        stack: 'all',
        data: [],
        itemStyle: { color: downColor },
        waterfallPlaceholder: true,
    }
    const originalData = waterfallSeries.data
    let cumulativeValue = 0
    const newBaseData = []
    originalData.forEach((value, index) => {
        const isStartValue = index === 0
        const isEndValue = index === originalData.length - 1
        const isEndpoint = isStartValue || isEndValue
        if (value >= 0) {
            positive.data.push(
                isEndpoint
                    ? { value, itemStyle: { color: isStartValue ? startColor : endColor } }
                    : value
            )
            negative.data.push(0)
            newBaseData.push(cumulativeValue)
        } else {
            negative.data.push(
                isEndpoint
                    ? { value: -value, itemStyle: { color: isStartValue ? startColor : endColor } }
                    : -value
            )
            positive.data.push(0)
            newBaseData.push(cumulativeValue + value)
        }
        cumulativeValue += value
    })
    waterfallSeries.data = newBaseData
    parsedSeries.push(positive, negative)
    return waterfallSeries
}

export const parseSeries = (summaryConfig, summaryData, themedOverrides, defaultTheme) => {
    const dataSeries = summaryData.dataset
        ? cloneDeep(summaryData.dataset.series)
        : cloneDeep(summaryData.series)
    let parsedSeries = []
    if (summaryConfig.seriesType === 'scatter') {
        // scatter is so different we need to handle it different
        // also it is not mix-able with other chart types
        parsedSeries = parseScatterSeries(summaryData.dataset ? summaryData.dataset : summaryData)
    } else {
        dataSeries.forEach((series, index) => {
            let seriesType =
                summaryConfig?.seriesTypeMap?.get(series.metric) || summaryConfig.seriesType
            const parseSeriesType = {
                column: () =>
                    parseColumnSeries(series, index, summaryConfig, summaryData, themedOverrides),
                line: () => parseLineSeries(series, index, summaryData, themedOverrides),
                area: () => parseAreaSeries(series, index, summaryData, themedOverrides),
                bar: () =>
                    parseBarSeries(series, index, summaryConfig, summaryData, themedOverrides),
                pie: () => parsePieSeries(series, index, summaryData, themedOverrides),
                donut: () => parseDonutSeries(series, index, summaryData, themedOverrides),
                heatmap: () => parseHeatmapSeries(series, index, summaryData),
                pictorialBar: () =>
                    parsePictorialSeries(series, index, summaryData, themedOverrides),
                gauge: () => parseGaugeSeries(series, index, summaryData, themedOverrides),
                radialBar: () => parseRadialBarSeries(series, index, summaryData, themedOverrides),
                waterfall: () =>
                    parseWaterfallSeries(
                        series,
                        index,
                        parsedSeries,
                        summaryData,
                        themedOverrides,
                        defaultTheme
                    ),
            }
            parsedSeries.push(parseSeriesType[seriesType]())
        })
    }
    return parsedSeries
}
